import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/App";
import { toast } from "sonner";
import { 
  Search, 
  CheckCircle2, 
  AlertCircle,
  Building2,
  FileText,
  Calendar,
  User,
  Shield,
  Loader2,
  ArrowLeft
} from 'lucide-react';

const TrackPacket = () => {
  const [applicationId, setApplicationId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!applicationId.trim()) {
      toast.error("Please enter an Application ID");
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const res = await fetch(`${API}/sponsorship/track/${applicationId.trim()}`);
      
      if (res.ok) {
        const data = await res.json();
        setResult(data);
      } else if (res.status === 404) {
        setError("No sponsorship packet found with this Application ID. Please check the ID and try again.");
      } else {
        setError("Unable to verify packet. Please try again later.");
      }
    } catch (_err) {
      setError("Connection error. Please check your internet and try again.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'intake_pending': { label: 'Intake Pending', color: 'bg-amber-100 text-amber-700' },
      'intake_complete': { label: 'Ready to Generate', color: 'bg-blue-100 text-blue-700' },
      'packet_generated': { label: 'Packet Generated', color: 'bg-emerald-100 text-emerald-700' },
      'enrolled': { label: 'Enrolled in Program', color: 'bg-purple-100 text-purple-700' }
    };
    const config = statusConfig[status] || { label: status, color: 'bg-neutral-100 text-neutral-700' };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="/mbc-logo.png" 
                alt="My Biz Consulting" 
                className="h-10 w-auto"
              />
              <span className="text-white font-semibold hidden sm:inline">Business Solutions Center</span>
            </Link>
            <Link to="/sponsorship">
              <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Gateway
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 rounded-full border border-emerald-500/20 mb-6">
            <Shield className="w-4 h-4 text-emerald-400" />
            <span className="text-sm font-medium text-emerald-400">Institutional Verification</span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Track & Verify Sponsorship Packet
          </h1>
          
          <p className="text-neutral-400 max-w-2xl mx-auto">
            Financial institutions can verify the authenticity of sponsorship packets submitted by applicants. 
            Enter the Application ID found on the sponsorship documentation.
          </p>
        </div>

        {/* Search Form */}
        <Card className="bg-white/5 border-white/10 mb-8">
          <CardContent className="p-6">
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Label htmlFor="appId" className="text-neutral-400 mb-2 block">Application ID</Label>
                <Input
                  id="appId"
                  value={applicationId}
                  onChange={(e) => setApplicationId(e.target.value)}
                  placeholder="e.g., sponsor_abc123def456"
                  className="bg-white/10 border-white/20 text-white placeholder:text-neutral-500"
                  data-testid="track-packet-input"
                />
              </div>
              <div className="flex items-end">
                <Button 
                  type="submit"
                  disabled={loading}
                  className="bg-[#39FF14] text-black hover:bg-[#32e612] font-semibold w-full sm:w-auto"
                  data-testid="track-packet-search-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      Verify Packet
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Error State */}
        {error && (
          <Card className="bg-red-500/10 border-red-500/20 mb-8" data-testid="track-packet-error">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">Verification Failed</h3>
                  <p className="text-neutral-400">{error}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Result */}
        {result && (
          <Card className="bg-white/5 border-emerald-500/30" data-testid="track-packet-result">
            <CardHeader className="border-b border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <CardTitle className="text-white">Verified Sponsorship Packet</CardTitle>
                    <CardDescription className="text-neutral-400">
                      This packet is authentic and issued by My Biz Consulting LLC
                    </CardDescription>
                  </div>
                </div>
                {getStatusBadge(result.status)}
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Building2 className="w-5 h-5 text-emerald-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-neutral-500">Business Name</p>
                      <p className="text-white font-medium">{result.business_name}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 text-emerald-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-neutral-500">Owner Name</p>
                      <p className="text-white font-medium">{result.owner_name}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-emerald-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-neutral-500">Selected Engagement</p>
                      <p className="text-white font-medium">{result.engagement_name}</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-emerald-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-neutral-500">Application Date</p>
                      <p className="text-white font-medium">{result.created_at}</p>
                    </div>
                  </div>
                  {result.packet_generated_at && (
                    <div className="flex items-start gap-3">
                      <FileText className="w-5 h-5 text-emerald-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-neutral-500">Packet Generated</p>
                        <p className="text-white font-medium">{result.packet_generated_at}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-emerald-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-neutral-500">Application ID</p>
                      <p className="text-white font-mono text-sm">{result.application_id}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="bg-emerald-500/10 rounded-lg p-4">
                  <p className="text-sm text-emerald-400">
                    <strong>Institutional Notice:</strong> This applicant has purchased a Funding Readiness Packet 
                    through My Biz Consulting LLC&apos;s Business Solutions Center. The engagement program includes 
                    consultant-led oversight, milestone tracking, and structured reporting designed to support 
                    responsible capital deployment.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info Section */}
        {!result && !error && (
          <div className="grid sm:grid-cols-2 gap-6 mt-12">
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-6">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-emerald-400" />
                  For Financial Institutions
                </h3>
                <p className="text-sm text-neutral-400">
                  Use this tool to verify that sponsorship packets submitted by loan applicants are authentic 
                  and issued by My Biz Consulting LLC. Each packet contains a unique Application ID.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-6">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-400" />
                  What We Verify
                </h3>
                <ul className="text-sm text-neutral-400 space-y-2">
                  <li>• Business name and owner</li>
                  <li>• Selected engagement program</li>
                  <li>• Application and generation dates</li>
                  <li>• Current enrollment status</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-white/10 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-neutral-500 text-sm">
            Questions about verification? Contact us at{' '}
            <a href="mailto:support@mybizconsulting.net" className="text-emerald-400 hover:underline">
              support@mybizconsulting.net
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TrackPacket;
